import React, { useState } from 'react';
import { useFirebase } from '../../lib/firebase/FirebaseProvider';

interface SignUpFormProps {
  onToggleMode: () => void;
}

export const SignUpForm: React.FC<SignUpFormProps> = ({ onToggleMode }) => {
  const { signUpWithEmail, signInWithGoogle, loading, error } = useFirebase();
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    confirmPassword: '',
    displayName: '',
  });
  const [formErrors, setFormErrors] = useState<Record<string, string>>({});

  const validateForm = () => {
    const errors: Record<string, string> = {};

    if (!formData.displayName.trim()) {
      errors.displayName = 'Display name is required';
    }

    if (!formData.email.trim()) {
      errors.email = 'Email is required';
    } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
      errors.email = 'Please enter a valid email address';
    }

    if (!formData.password) {
      errors.password = 'Password is required';
    } else if (formData.password.length < 6) {
      errors.password = 'Password must be at least 6 characters';
    }

    if (formData.password !== formData.confirmPassword) {
      errors.confirmPassword = 'Passwords do not match';
    }

    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }

    await signUpWithEmail(formData.email, formData.password, formData.displayName);
  };

  const handleGoogleSignIn = async () => {
    await signInWithGoogle();
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setFormData(prev => ({
      ...prev,
      [e.target.name]: e.target.value,
    }));
    
    // Clear field-specific error when user starts typing
    if (formErrors[e.target.name]) {
      setFormErrors(prev => ({
        ...prev,
        [e.target.name]: '',
      }));
    }
  };

  return (
    <div className="w-full max-w-md mx-auto">
      <div className="bg-teddy shadow-card rounded-lg px-8 pt-6 pb-8 mb-4">
        <h2 className="text-2xl font-bold text-center text-urban mb-6">
          Join Curbside Quest
        </h2>

        {error && (
          <div className="bg-rust-100 border border-rust-400 text-rust-700 px-4 py-3 rounded mb-4">
            {error}
          </div>
        )}

        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label htmlFor="displayName" className="block text-urban text-sm font-bold mb-2">
              Display Name
            </label>
            <input
              type="text"
              id="displayName"
              name="displayName"
              value={formData.displayName}
              onChange={handleInputChange}
              className={`shadow-card appearance-none border rounded w-full py-2 px-3 bg-sky-neutral text-urban leading-tight focus:outline-none focus:border-cardboard focus:ring-2 focus:ring-treasure-200 ${
                formErrors.displayName ? 'border-rust' : 'border-urban-300'
              }`}
              placeholder="Enter your display name"
              required
            />
            {formErrors.displayName && (
              <p className="text-rust text-xs italic mt-1">{formErrors.displayName}</p>
            )}
          </div>

          <div>
            <label htmlFor="email" className="block text-urban text-sm font-bold mb-2">
              Email
            </label>
            <input
              type="email"
              id="email"
              name="email"
              value={formData.email}
              onChange={handleInputChange}
              className={`shadow-card appearance-none border rounded w-full py-2 px-3 bg-sky-neutral text-urban leading-tight focus:outline-none focus:border-cardboard focus:ring-2 focus:ring-treasure-200 ${
                formErrors.email ? 'border-rust' : 'border-urban-300'
              }`}
              placeholder="Enter your email"
              required
            />
            {formErrors.email && (
              <p className="text-rust text-xs italic mt-1">{formErrors.email}</p>
            )}
          </div>

          <div>
            <label htmlFor="password" className="block text-urban text-sm font-bold mb-2">
              Password
            </label>
            <input
              type="password"
              id="password"
              name="password"
              value={formData.password}
              onChange={handleInputChange}
              className={`shadow-card appearance-none border rounded w-full py-2 px-3 bg-sky-neutral text-urban leading-tight focus:outline-none focus:border-cardboard focus:ring-2 focus:ring-treasure-200 ${
                formErrors.password ? 'border-rust' : 'border-urban-300'
              }`}
              placeholder="Enter your password"
              required
            />
            {formErrors.password && (
              <p className="text-rust text-xs italic mt-1">{formErrors.password}</p>
            )}
          </div>

          <div>
            <label htmlFor="confirmPassword" className="block text-urban text-sm font-bold mb-2">
              Confirm Password
            </label>
            <input
              type="password"
              id="confirmPassword"
              name="confirmPassword"
              value={formData.confirmPassword}
              onChange={handleInputChange}
              className={`shadow-card appearance-none border rounded w-full py-2 px-3 bg-sky-neutral text-urban leading-tight focus:outline-none focus:border-cardboard focus:ring-2 focus:ring-treasure-200 ${
                formErrors.confirmPassword ? 'border-rust' : 'border-urban-300'
              }`}
              placeholder="Confirm your password"
              required
            />
            {formErrors.confirmPassword && (
              <p className="text-rust text-xs italic mt-1">{formErrors.confirmPassword}</p>
            )}
          </div>

          <button
            type="submit"
            disabled={loading}
            className="w-full bg-verge hover:bg-verge-600 text-white font-bold py-2 px-4 rounded focus:outline-none focus:shadow-outline disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {loading ? 'Creating Account...' : 'Create Account'}
          </button>
        </form>

        <div className="mt-6">
          <div className="relative">
            <div className="absolute inset-0 flex items-center">
              <div className="w-full border-t border-urban-300" />
            </div>
            <div className="relative flex justify-center text-sm">
              <span className="px-2 bg-teddy text-urban-300">Or continue with</span>
            </div>
          </div>

          <button
            onClick={handleGoogleSignIn}
            disabled={loading}
            className="mt-4 w-full bg-rust hover:bg-rust-600 text-white font-bold py-2 px-4 rounded focus:outline-none focus:shadow-outline disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center"
          >
            <svg className="w-5 h-5 mr-2" viewBox="0 0 24 24">
              <path
                fill="currentColor"
                d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
              />
              <path
                fill="currentColor"
                d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
              />
              <path
                fill="currentColor"
                d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
              />
              <path
                fill="currentColor"
                d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
              />
            </svg>
            {loading ? 'Signing Up...' : 'Sign Up with Google'}
          </button>
        </div>

        <div className="text-center mt-6">
          <span className="text-urban-300">Already have an account? </span>
          <button
            onClick={onToggleMode}
            className="text-cardboard hover:text-cardboard-700 font-medium"
          >
            Sign In
          </button>
        </div>
      </div>
    </div>
  );
};